/**
 * Corporate Credentials Vault — client-side crypto (Web Crypto + libsodium Argon2id).
 * Never send KEK or DEK over the network.
 */
import _sodium from 'libsodium-wrappers-sumo';

export function bytesToBase64(bytes) {
  let bin = '';
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < u8.length; i += 1) bin += String.fromCharCode(u8[i]);
  return btoa(bin);
}

export function base64ToBytes(b64) {
  const bin = atob(String(b64).trim());
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * @returns {{ kek: Uint8Array, authHash: string hex }}
 */
export async function deriveKeys(masterPassword, saltUint8) {
  await _sodium.ready;
  const sodium = _sodium;
  const pwd = sodium.from_string(masterPassword);
  const kek = sodium.crypto_pwhash(
    32,
    pwd,
    saltUint8,
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
  const pwdAuth = sodium.from_string(`${masterPassword}auth`);
  const authRaw = sodium.crypto_pwhash(
    32,
    pwdAuth,
    saltUint8,
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
  sodium.memzero(pwd);
  sodium.memzero(pwdAuth);
  return { kek, authHash: sodium.to_hex(authRaw) };
}

export async function randomSaltBytes() {
  await _sodium.ready;
  return _sodium.randombytes_buf(_sodium.crypto_pwhash_SALTBYTES);
}

export async function randomDek() {
  await _sodium.ready;
  return _sodium.randombytes_buf(32);
}

async function importAesGcmKey(rawKeyBytes) {
  return crypto.subtle.importKey('raw', rawKeyBytes, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/** Encrypt arbitrary bytes with AES-256-GCM (DEK wrapping). */
export async function encryptBinaryWithKey(plaintextBytes, keyBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await importAesGcmKey(keyBytes);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, plaintextBytes);
  return {
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(ct)),
  };
}

export async function decryptBinaryWithKey(ivBase64, dataBase64, keyBytes) {
  const iv = base64ToBytes(ivBase64);
  const data = base64ToBytes(dataBase64);
  const cryptoKey = await importAesGcmKey(keyBytes);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
  return new Uint8Array(pt);
}

/** Wrap DEK with KEK for POST /credential-vault/crypto/setup */
export async function wrapDekWithKek(dekBytes, kekBytes) {
  return encryptBinaryWithKey(dekBytes, kekBytes);
}

/** Unwrap DEK after unlock response */
export async function unwrapDekFromPayload(encryptedDekPayload, kekBytes) {
  const payload =
    typeof encryptedDekPayload === 'string' ? JSON.parse(encryptedDekPayload) : encryptedDekPayload;
  return decryptBinaryWithKey(payload.iv, payload.data, kekBytes);
}

export async function encryptCredential(plaintext, dekBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const cryptoKey = await importAesGcmKey(dekBytes);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, encoded);
  return {
    iv: bytesToBase64(iv),
    data: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

export async function decryptCredential(encryptedPayload, dekBytes) {
  const iv = base64ToBytes(encryptedPayload.iv);
  const data = base64ToBytes(encryptedPayload.data);
  const cryptoKey = await importAesGcmKey(dekBytes);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
  return new TextDecoder().decode(plaintext);
}

/** Full corporate vault setup payload for API + DEK for immediate session use */
export async function prepareCorporateVaultSetup(masterPassword, confirmPassword) {
  if (!masterPassword || masterPassword !== confirmPassword) {
    throw new Error('Passwords do not match');
  }
  const saltBytes = await randomSaltBytes();
  const kdfSalt = bytesToBase64(saltBytes);
  const { kek, authHash } = await deriveKeys(masterPassword, saltBytes);
  const dek = await randomDek();
  const enc = await wrapDekWithKek(dek, kek);
  const encryptedDek = JSON.stringify(enc);
  await _sodium.ready;
  _sodium.memzero(kek);
  return { kdfSalt, authHash, encryptedDek, dek };
}

/** Per-user private vault: same KDF/wrap flow as corporate; different API storage. */
export async function preparePersonalVaultSetup(vaultPassword, confirmPassword) {
  return prepareCorporateVaultSetup(vaultPassword, confirmPassword);
}

/**
 * Build payload for POST /credential-vault/crypto/reset-master-password (same KDF salt, new KEK wrapping same DEK).
 * @param {string} kdfSaltBase64 - Existing corporate salt from API
 * @param {string|object} encryptedDekPayload - Current wrapped DEK from unlock response
 */
export async function prepareMasterPasswordRotation(
  currentMasterPassword,
  newMasterPassword,
  confirmNewPassword,
  kdfSaltBase64,
  encryptedDekPayload
) {
  if (!newMasterPassword || newMasterPassword !== confirmNewPassword) {
    throw new Error('New passwords do not match');
  }
  if (!currentMasterPassword) {
    throw new Error('Current master password is required');
  }
  if (currentMasterPassword === newMasterPassword) {
    throw new Error('New master password must be different from the current password');
  }
  const saltBytes = base64ToBytes(kdfSaltBase64);
  const { kek: oldKek, authHash: currentAuthHash } = await deriveKeys(currentMasterPassword, saltBytes);
  let dek;
  try {
    dek = await unwrapDekFromPayload(encryptedDekPayload, oldKek);
  } finally {
    await zeroBytes(oldKek);
  }
  const { kek: newKek, authHash: newAuthHash } = await deriveKeys(newMasterPassword, saltBytes);
  let wrapped;
  try {
    wrapped = await wrapDekWithKek(dek, newKek);
  } finally {
    await zeroBytes(newKek);
    await zeroBytes(dek);
  }
  return {
    currentAuthHash,
    newAuthHash,
    newEncryptedDek: JSON.stringify(wrapped),
  };
}

/** Same crypto as corporate master rotation; personal vault keeps the same KDF salt on the server. */
export async function preparePersonalVaultPasswordRotation(
  currentPassword,
  newPassword,
  confirmNewPassword,
  kdfSaltBase64,
  encryptedDekPayload
) {
  return prepareMasterPasswordRotation(
    currentPassword,
    newPassword,
    confirmNewPassword,
    kdfSaltBase64,
    encryptedDekPayload
  );
}

export async function zeroBytes(buf) {
  if (!buf || !(buf instanceof Uint8Array)) return;
  await _sodium.ready;
  _sodium.memzero(buf);
}

export function getJwtRole() {
  try {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    let raw = rememberMe ? localStorage.getItem('accessToken') : sessionStorage.getItem('accessToken');
    if (!raw) raw = localStorage.getItem('tempAccessToken');
    if (!raw) return null;
    let jwtStr;
    try {
      const parsed = JSON.parse(raw);
      jwtStr = typeof parsed === 'string' ? parsed : parsed?.accessToken || parsed?.token;
    } catch {
      jwtStr = raw;
    }
    if (!jwtStr || typeof jwtStr !== 'string') return null;
    const payload = JSON.parse(atob(jwtStr.split('.')[1]));
    return payload.role || payload.user_type || null;
  } catch {
    return null;
  }
}

export function isCorporateAdminRole() {
  const r = getJwtRole();
  return r === 'corporate_admin' || r === 'admin';
}

/**
 * Master-password (ZK) vault is for corporate accounts and org-linked users only.
 * Others should see a contact-administrator message instead of unlock/setup.
 */
export function isCorporateVaultAccount() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const accountType = String(u?.type || u?.userType || '').toLowerCase();
    if (accountType === 'corporate') return true;
    const cid = u?.corporateId ?? u?.CorporateId;
    if (cid != null && cid !== '') return true;
    return false;
  } catch {
    return false;
  }
}
